// The claim set behind "Every claim" + the permanent claim record (rcm-25,
// rcm-26, 00-ds-09 §3/§4, D148/D149). One seed (rcm-31 §2): the 40 claims in
// Claims to review, the hand-written messy-path claims, a few named cases, and
// ~2,400 generated ones, all dated on or before today (Oct 18, 2026) and built
// backwards from their state (claimBuilder.ts). Entirely client-side; the real
// claim index is server-owned (§8).

import type { Claim } from './types';
import { buildTrainingClaims } from './fixtures';
import { CURATED_CLAIMS } from './curatedClaims';
import { SPECIAL_CLAIMS, SPECIAL_IDS } from './specialClaims';
import {
  buildClaim,
  buildReplacementPair,
  clock12,
  hashSeed,
  isoOf,
  moneyOf,
  parseIso,
  fullOf,
  shortOf,
} from './claimBuilder';
import { rng } from '@/components/lists';
import { CLAIMS_TODAY } from './today';
import { isOutOfNetwork } from '@/features/networks/store';
import { isPaidToPatientPosted } from '@/features/networks/paidToPatient';
import { IRIS_CLAIM_ID, IRIS_CLOSED } from './paysPatientClaims';
import { clockNow, fileByOf } from './utils';
import { PMS } from '@/features/shared/pms';

/** One state per claim, matching the /claims/all status filter (rcm-25 §4). */
export type ClaimStatus =
  | 'waiting'
  | 'on-hold'
  | 'checking-if-sent'
  | 'sent'
  | 'rejected'
  | 'with-payer'
  | 'late'
  | 'paid-short'
  | 'denied'
  | 'appealed'
  | 'paid'
  | 'posted'
  | 'deposited'
  | 'paid-to-patient'
  | 'taken-back'
  | 'written-off'
  | 'not-billed'
  | 'replaced';

export const CLAIM_STATUSES: ClaimStatus[] = [
  'waiting',
  'on-hold',
  'checking-if-sent',
  'sent',
  'rejected',
  'with-payer',
  'late',
  'paid-short',
  'denied',
  'appealed',
  'paid',
  'posted',
  'deposited',
  'paid-to-patient',
  'taken-back',
  'written-off',
  'not-billed',
  'replaced',
];

export const CLAIM_STATUS_LABEL: Record<ClaimStatus, string> = {
  waiting: 'Waiting for review',
  'on-hold': 'On hold',
  'checking-if-sent': 'Checking if it was sent',
  sent: 'Sent',
  rejected: 'Rejected',
  'with-payer': 'With the payer',
  late: 'Late',
  'paid-short': 'Paid short',
  denied: 'Denied',
  appealed: 'Appealed',
  paid: 'Paid, to post',
  posted: 'Posted',
  deposited: 'Deposited',
  'paid-to-patient': 'Paid to the patient',
  'taken-back': 'Taken back',
  'written-off': 'Written off',
  'not-billed': 'Not billed',
  replaced: 'Replaced',
};

/** Dot colour for the "Where it is" cell: teal in-progress, green paid, amber
 *  needs-a-person or a warning (Checking if it was sent), red denied or taken back,
 *  grey closed (rcm-25 §5). */
export type Tone = 'teal' | 'green' | 'amber' | 'red' | 'grey';

export interface HistoryProcedure {
  code: string;
  desc: string;
  tooth: string | null;
  fee: number;
}

/** One dot on the claim's Path row (rcm-26 §3). */
export interface PathStep {
  label: string;
  date: string;
  /** Red dot (rejected, denied, taken back). */
  bad?: boolean;
  /** Hollow dot: nothing on record for this step. Shows "No record", never a date. */
  none?: boolean;
  /** Outlined dot: happened outside ELVA (before ELVA saw the claim). */
  outline?: boolean;
  /** Repeated step: one dot with a count ("Sent ×2"). */
  count?: number;
}

/** One line of "Everything that happened" (rcm-26 §3). */
export interface TimelineEntry {
  stamp: string;
  /** yyyy-mm-dd of the event; filled in for every entry on load. */
  iso?: string;
  ok: boolean;
  lead: string;
  detail?: string;
  who: string;
  /** Recorded by Dentrix before ELVA saw the claim: labelled "from Dentrix", no Log link. */
  fromDentrix?: boolean;
  /** Red text for a problem event (a payment taken back). */
  bad?: boolean;
}

/** One payment line in "The money". A negative amount is a payment taken back. */
export interface PaymentLine {
  amount: number;
  date: string;
}

/** A linked record: "Secondary claim with Delta Dental: CLM-31384". */
export interface ClaimLink {
  label: string;
  id: string;
}

/** The compact-band sentence on the record page (rcm-26 §2). */
export interface BandSentence {
  pre: string;
  em?: string;
  post?: string;
  /** mint em (good news) vs amber (needs a person). */
  mint: boolean;
}

/** Where the claim is still open. `to` is the page root; the record page links
 *  to this claim on that page when the page knows the id. */
export interface OpenLink {
  label: string;
  to: string;
  page: 'claims' | 'ar' | 'denied' | 'posting';
}

export interface HistoryClaim {
  /** PMS claim number, e.g. "CLM-26431". */
  id: string;
  patient: string;
  /** Payer short name, e.g. "Cigna". */
  payer: string;
  /** Plan name, e.g. "Cigna DPPO". */
  plan: string;
  /** Payer's own claim number, e.g. "CG-77310452". */
  payerClaimNo: string;
  billed: number;
  /** Paid to date; null until anything is paid ("—" in the table). */
  paid: number | null;
  /** What the payer allowed. null until the payer answers (then the money card
   *  shows "Expected … · fee schedule"); $0.00 on a denial. */
  allowed: number | null;
  /** What the fee schedule would allow; shown in grey before the payer answers. */
  expectedAllowed?: number;
  /** Allowed − insurer paid − other adjustments (in network). Never billed − paid. */
  patientOwes: number;
  /** Contractual write-off (billed − allowed); null when the payer hasn't priced it. */
  writeOff: number | null;
  /** Each payment as its own line; a recoupment is negative. Falls back to `paid`. */
  payments?: PaymentLine[];
  /** Small note under Patient owes. */
  owesNote?: string;
  /** Paid short: what the plan was expected to pay, and how much is short. */
  expectedPay?: number;
  short?: number;
  /** Written off: the amount and why. */
  writtenOff?: { amount: number; reason: string };
  /** Set when ELVA didn't start the claim: "ELVA first saw this claim on {date}". */
  firstSeen?: string;
  /** Secondary and corrected-claim links, both ways. */
  links?: ClaimLink[];
  visitIso: string;
  /** "Oct 12" */
  visitShort: string;
  /** "Oct 12, 2026" */
  visitFull: string;
  /** The record's last event, so the list and the record agree. */
  lastChangeIso: string;
  /** "Oct 12" */
  lastChangeLabel: string;
  status: ClaimStatus;
  tone: Tone;
  /** The words after the dot: exactly one label per state (rcm-25 §5). */
  where: string;
  needsPerson: boolean;
  memberId: string;
  provider: string;
  /** Display code list, e.g. ["D2740", "D2950"]. */
  codes: string[];
  procedures: HistoryProcedure[];
  // ---- the whole life (record page) ----
  path: PathStep[];
  /** Grey "next" step for an open claim, e.g. "Waiting for Cigna". */
  nextStep?: string;
  /** Words under the grey next step, e.g. "usually by Jul 5". */
  nextNote?: string;
  timeline: TimelineEntry[];
  payerPaidDate?: string;
  depositDate?: string;
  closedDate?: string;
  /** When still open: where the work is, with a link back. */
  openIn?: OpenLink;
  /** True for the claims that are also in Claims to review. */
  fromQueue?: boolean;
  /** Closed as Paid to the patient (D197): the payer sent its money to the patient, none to the practice. */
  paidToPatient?: { amount: number; date: string; paidAtVisit: number };
  /** Reason text under a hollow "Deposited" dot, e.g. "No bank statement uploaded after Sep 20". */
  noDepositReason?: string;
  band: BandSentence;
  /** A one-line status under the path, for a state with no page of its own (Checking if it was sent). */
  statusLine?: string;
  /** Replaces the "Open now in …" note in the aside when no page holds this claim. */
  asideNote?: string;
}

// --- Back-compat aliases (the record page's status badge helpers) -----------
export type HistoryStatus = ClaimStatus;
export const HISTORY_STATUSES = CLAIM_STATUSES;
export const HISTORY_STATUS_LABEL = CLAIM_STATUS_LABEL;

// ----------------------------------------------------------------------------
// Claims to review → Every claim (rcm-31 §2: one claim set)
// ----------------------------------------------------------------------------

const PAYER_FULL: Record<string, string> = { Delta: 'Delta Dental' };

function clockOf(id: string, i: number): string {
  const min = 6 * 60 + (hashSeed(`${id}|q${String(i)}`) % (11 * 60));
  const h24 = Math.floor(min / 60);
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${String(h)}:${String(min % 60).padStart(2, '0')} ${h24 >= 12 ? 'PM' : 'AM'}`;
}

/** D197: out of network with the payer and no assignment of benefits on file, so the payer pays the patient. */
function paysPatientSuffix(c: Claim): boolean {
  return c.assignmentOfBenefits === 'none' && isOutOfNetwork(c.provider, c.payer.short);
}

/** A claim still in the review queue, shaped like a permanent record. */
export function recordFromQueue(c: Claim): HistoryClaim {
  const visit = parseIso(c.serviceDate);
  const payer = PAYER_FULL[c.payer.short] ?? c.payer.short;
  const procedures: HistoryProcedure[] = c.procedures.map((p) => ({ code: p.code, desc: p.description, tooth: p.tooth, fee: p.fee }));
  const codes = [...new Set(procedures.map((p) => p.code))];
  const billed = procedures.reduce((s, p) => s + p.fee, 0);
  const m = moneyOf(procedures, 0.88);
  const first = c.patient.split(' ')[0] ?? c.patient;
  const P = `${first}’s`;
  const vS = shortOf(visit);
  const today = isoOf(CLAIMS_TODAY);
  const fileBy = fileByOf(c, 'training');
  const base: HistoryClaim = {
    id: c.id,
    patient: c.patient,
    payer,
    plan: c.payer.name,
    payerClaimNo: '',
    billed,
    paid: null,
    allowed: null,
    expectedAllowed: m.allowed,
    patientOwes: 0,
    writeOff: null,
    visitIso: c.serviceDate,
    visitShort: vS,
    visitFull: fullOf(visit),
    lastChangeIso: c.serviceDate,
    lastChangeLabel: vS,
    status: 'waiting',
    tone: 'amber',
    where: 'Waiting for review',
    needsPerson: true,
    memberId: c.memberId,
    provider: c.provider,
    codes,
    procedures,
    path: [{ label: 'Prepared', date: vS }],
    nextStep: 'Waiting for your review',
    nextNote: fileBy ? `Claims to review · ${fileBy.label}` : 'Claims to review',
    timeline: [
      { stamp: `${vS}, ${clockOf(c.id, 0)}`, iso: c.serviceDate, ok: true, lead: 'Prepared the claim', detail: `from ${PMS}’s draft`, who: 'ELVA' },
    ],
    openIn: { label: 'Claims to review', to: '/claims', page: 'claims' },
    fromQueue: true,
    band: { pre: `${P} claim of ${vS} is prepared and `, em: 'waiting for your review', post: '.', mint: false },
  };
  if (c.removed === 'confirmed') {
    const stamp = `${shortOf(CLAIMS_TODAY)}, ${clockNow()}`;
    return {
      ...base,
      status: 'sent',
      tone: 'teal',
      where: paysPatientSuffix(c) ? `With ${payer} · pays the patient` : `With ${payer}`,
      needsPerson: false,
      lastChangeIso: today,
      lastChangeLabel: shortOf(CLAIMS_TODAY),
      path: [{ label: 'Prepared', date: vS }, { label: 'Confirmed', date: shortOf(CLAIMS_TODAY) }, { label: 'Sent', date: shortOf(CLAIMS_TODAY) }],
      nextStep: `Waiting for ${payer}`,
      nextNote: 'accepts the claim',
      timeline: [
        ...base.timeline,
        { stamp, iso: today, ok: true, lead: 'Confirmed the claim', who: 'Ava Thompson (admin)' },
        { stamp, iso: today, ok: true, lead: `Sent it from ${PMS} · the clearinghouse accepted it`, who: 'ELVA' },
      ],
      openIn: { label: 'Insurance A/R', to: '/ar', page: 'ar' },
      band: { pre: `${P} claim of ${vS} was sent to ${payer}.`, mint: false },
    };
  }
  if (c.removed === 'not_billed') {
    const { nextStep, nextNote, ...rest } = base;
    void nextStep;
    void nextNote;
    return {
      ...rest,
      status: 'not-billed',
      tone: 'grey',
      where: 'Not billed',
      needsPerson: false,
      lastChangeIso: today,
      lastChangeLabel: shortOf(CLAIMS_TODAY),
      path: [{ label: 'Prepared', date: vS }, { label: 'Not billed', date: shortOf(CLAIMS_TODAY) }],
      timeline: [...base.timeline, { stamp: `${shortOf(CLAIMS_TODAY)}, ${clockNow()}`, iso: today, ok: true, lead: 'Marked the claim not billed', who: 'Ava Thompson (admin)' }],
      band: { pre: `${P} claim of ${vS} was not billed.`, mint: false },
    };
  }
  if (c.held) {
    return {
      ...base,
      status: 'on-hold',
      tone: 'teal',
      where: `On hold · ${c.held.reason}`,
      needsPerson: false,
      nextStep: 'On hold',
      nextNote: c.held.until ? `comes back ${c.held.until}` : 'until it is released',
      band: { pre: `${P} claim of ${vS} is on hold: ${c.held.reason.toLowerCase()}.`, mint: false },
    };
  }
  return base;
}

// ----------------------------------------------------------------------------
// The full set
// ----------------------------------------------------------------------------

type GenState = Parameters<typeof buildClaim>[0]['state'];
interface Pick {
  state: GenState;
  variant?: NonNullable<Parameters<typeof buildClaim>[0]['variant']>;
}

/** Weighted state for a generated claim, seeded off its id. Waiting-for-review
 *  claims are only the ones in the queue, so counts agree with Claims to review. */
function stateFor(id: string): Pick {
  const x = rng(hashSeed(`${id}|state`))();
  const table: [number, Pick][] = [
    [0.004, { state: 'on-hold' }],
    [0.006, { state: 'sent' }],
    [0.002, { state: 'rejected' }],
    [0.04, { state: 'with-payer' }],
    [0.008, { state: 'late' }],
    [0.002, { state: 'limit' }],
    [0.01, { state: 'paid-short' }],
    [0.004, { state: 'denied' }],
    [0.012, { state: 'appealed' }],
    [0.004, { state: 'paid' }],
    [0.006, { state: 'posted' }],
    [0.004, { state: 'taken-back' }],
    [0.03, { state: 'written-off' }],
    [0.01, { state: 'not-billed' }],
    [0.008, { state: 'replaced' }],
    [0.04, { state: 'deposited', variant: 'after-appeal' }],
  ];
  let acc = 0;
  for (const [w, p] of table) {
    acc += w;
    if (x < acc) return p;
  }
  return { state: 'deposited' };
}

const QUEUE_CLAIMS: HistoryClaim[] = buildTrainingClaims().map(recordFromQueue);

/** Calendar of the first-and-last event, to keep the list's Last change equal to the record's. */
const MONTH_IDX: Record<string, number> = { Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6, Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12 };
function isoFromStamp(stamp: string): string {
  const m = /^([A-Z][a-z]{2}) (\d{1,2})(?:, (\d{4}))?/.exec(stamp);
  if (!m) return '2026-01-01';
  const year = m[3] ?? '2026';
  return `${year}-${String(MONTH_IDX[m[1] ?? 'Jan'] ?? 1).padStart(2, '0')}-${(m[2] ?? '1').padStart(2, '0')}`;
}

/** Spreads a hand-written claim's clock times by a hash of its id (rcm-31 §3),
 *  keeping each day's events in order. Entries with no time are left alone. */
function retime(c: HistoryClaim): HistoryClaim {
  let prevIso = '';
  let prevMin = 0;
  const timeline = c.timeline.map((e, i) => {
    const m = /^(.*), \d{1,2}:\d{2} (?:AM|PM)$/.exec(e.stamp);
    if (!m) return e;
    const iso = e.iso ?? isoFromStamp(e.stamp);
    let min = 6 * 60 + (hashSeed(`${c.id}|${String(i)}`) % (12 * 60));
    if (iso === prevIso && min <= prevMin) min = prevMin + 3 + (hashSeed(`${c.id}|t${String(i)}`) % 40);
    prevIso = iso;
    prevMin = min;
    return { ...e, iso, stamp: `${m[1] ?? ''}, ${clock12(min)}` };
  });
  return { ...c, timeline };
}

/** Fills each event's date and makes Last change the record's last event. */
function normalize(c: HistoryClaim): HistoryClaim {
  const timeline = c.timeline.map((e) => (e.iso ? e : { ...e, iso: isoFromStamp(e.stamp) }));
  const last = timeline[timeline.length - 1];
  if (!last?.iso) return { ...c, timeline };
  const dt = parseIso(last.iso);
  return { ...c, timeline, lastChangeIso: last.iso, lastChangeLabel: shortOf(dt) };
}

function generateAll(): HistoryClaim[] {
  const used = new Set<string>([...QUEUE_CLAIMS, ...CURATED_CLAIMS, ...SPECIAL_CLAIMS].map((c) => c.patient));
  const out: HistoryClaim[] = [];
  let pair = 0;
  for (let i = 0; i < 2400; i++) {
    const id = `CLM-${String(26400 + i)}`;
    if (SPECIAL_IDS.has(id)) continue;
    const pick_ = stateFor(id);
    if (pick_.state === 'replaced') {
      const [oldC, newC] = buildReplacementPair(id, `CLM-${String(29000 + pair)}`, { usedNames: used });
      pair++;
      used.add(oldC.patient);
      out.push(oldC, newC);
      continue;
    }
    const c = buildClaim({ id, state: pick_.state, ...(pick_.variant ? { variant: pick_.variant } : {}), usedNames: used });
    used.add(c.patient);
    out.push(c);
  }
  return out;
}

/** Every claim, newest sources first: the review queue, the messy-path cases,
 *  the named cases, then the generated history. */
export const HISTORY_CLAIMS: HistoryClaim[] = [
  ...QUEUE_CLAIMS,
  ...CURATED_CLAIMS.map(retime),
  ...SPECIAL_CLAIMS,
  ...generateAll(),
].map(normalize);

const BY_ID = new Map(HISTORY_CLAIMS.map((c) => [c.id, c]));

// Iris Calder's claim is open until Posting posts it, then closed as Paid to the
// patient (H2 §1). Everything else is static, so only that one row is swapped.
const IRIS_CLOSED_N = normalize(IRIS_CLOSED);
const WITH_IRIS_CLOSED: HistoryClaim[] = HISTORY_CLAIMS.map((c) => (c.id === IRIS_CLAIM_ID ? IRIS_CLOSED_N : c));

/** Every claim as it stands now (reads the shared paid-to-the-patient state). Re-render on `usePaidToPatientVersion`. */
export function getHistoryClaims(): HistoryClaim[] {
  return isPaidToPatientPosted(IRIS_CLAIM_ID) ? WITH_IRIS_CLOSED : HISTORY_CLAIMS;
}

/** A row from the big fixture, or null when the claim number isn't in it. */
export function findHistoryClaim(id: string): HistoryClaim | null {
  if (id === IRIS_CLAIM_ID && isPaidToPatientPosted(IRIS_CLAIM_ID)) return IRIS_CLOSED_N;
  return BY_ID.get(id) ?? null;
}

/**
 * Build a believable record from an id that isn't in the fixture (D149: every
 * /claims/record/:id link resolves). Deterministic, seeded off the id.
 */
export function claimFromId(id: string): HistoryClaim {
  const p = stateFor(id);
  const state = p.state === 'replaced' ? 'deposited' : p.state;
  return normalize(buildClaim({ id, state, ...(p.variant ? { variant: p.variant } : {}) }));
}
